import type { SilkHomeCarouselSlide } from '@/api'

/** SaaS 租户功能模块(与 backend platform/features.go 对齐) */
export type TenantFeatureKey =
  | 'workbench'
  | 'studio_production'
  | 'studio_writer'
  | 'studio_creative'
  | 'contests'
  | 'copyright'
  | 'marketplace'
  | 'task_center'
  | 'learning_center'
  | 'contests_global_catalog'
  | 'tasks_global_catalog'

export interface TenantFeatures {
  workbench: boolean
  studio_production: boolean
  studio_writer: boolean
  studio_creative: boolean
  contests: boolean
  copyright: boolean
  marketplace: boolean
  task_center: boolean
  learning_center: boolean
  contests_global_catalog: boolean
  tasks_global_catalog: boolean
}

export const DEFAULT_TENANT_FEATURES: TenantFeatures = {
  workbench: true,
  studio_production: true,
  studio_writer: true,
  studio_creative: true,
  contests: true,
  copyright: true,
  marketplace: true,
  task_center: true,
  learning_center: false,
  contests_global_catalog: false,
  tasks_global_catalog: false,
}

export function normalizeTenantFeatures(raw?: Partial<TenantFeatures> | null): TenantFeatures {
  const workbench = raw?.workbench !== false
  const hasStudioKeys =
    raw != null &&
    ('studio_production' in raw || 'studio_writer' in raw || 'studio_creative' in raw)
  const studioProduction = hasStudioKeys ? raw?.studio_production !== false : workbench
  const studioWriter = hasStudioKeys ? raw?.studio_writer !== false : workbench
  const studioCreative = hasStudioKeys ? raw?.studio_creative !== false : workbench
  return {
    workbench,
    studio_production: studioProduction,
    studio_writer: studioWriter,
    studio_creative: studioCreative,
    contests: raw?.contests !== false,
    copyright: raw?.copyright !== false,
    marketplace: raw?.marketplace !== false,
    task_center: raw?.task_center !== false,
    learning_center: raw?.learning_center === true,
    contests_global_catalog: raw?.contests_global_catalog === true,
    tasks_global_catalog: raw?.tasks_global_catalog === true,
  }
}

export function isFeatureEnabled(features: TenantFeatures, key: TenantFeatureKey): boolean {
  if (
    key === 'learning_center' ||
    key === 'contests_global_catalog' ||
    key === 'tasks_global_catalog'
  ) {
    return features[key] === true
  }
  return features[key] !== false
}

/** 创作中心门户入口：任一 studio 模式或 workbench 横切能力开通即可 */
export function isStudioPortalEnabled(features: TenantFeatures): boolean {
  return (
    features.workbench ||
    features.studio_production ||
    features.studio_writer ||
    features.studio_creative
  )
}

/** 路由 path → 所需模块（画布路由已移除） */
export function featureForPath(pathname: string): TenantFeatureKey | null {
  const path = pathname.split('?')[0] ?? pathname
  if (path.startsWith('/contests')) return 'contests'
  if (path.startsWith('/copyright')) return 'copyright'
  if (path.startsWith('/marketplace')) return 'marketplace'
  if (path.startsWith('/tasks')) return 'task_center'
  if (path.startsWith('/learning')) return 'learning_center'
  if (path.startsWith('/subscription')) return 'workbench'
  if (path.startsWith('/account/storage')) return 'workbench'
  if (path.startsWith('/studio/productions')) return 'studio_production'
  if (path.startsWith('/studio/writer')) return 'studio_writer'
  if (path.startsWith('/studio/creative')) return 'studio_creative'
  if (path.startsWith('/studio/invites')) return 'studio_production'
  if (path.startsWith('/studio')) return null
  if (path.startsWith('/workbench')) return 'workbench'
  if (path.startsWith('/projects')) return 'workbench'
  if (path.startsWith('/aistudio')) return 'workbench'
  return null
}

const SLIDE_TYPE_FEATURE: Partial<Record<SilkHomeCarouselSlide['type'], TenantFeatureKey>> = {
  contest: 'contests',
  ranking: 'contests',
  feature: 'workbench',
  brand: 'workbench',
}

/** 首页轮播 slide 是否对当前租户可见 */
export function isCarouselSlideAllowed(
  slide: Pick<SilkHomeCarouselSlide, 'type' | 'cta_primary_to' | 'cta_secondary_to'>,
  features: TenantFeatures,
): boolean {
  const typeFeature = SLIDE_TYPE_FEATURE[slide.type]
  if (typeFeature && !isFeatureEnabled(features, typeFeature)) return false

  for (const to of [slide.cta_primary_to, slide.cta_secondary_to]) {
    if (!to) continue
    const required = featureForPath(to)
    if (required && !isFeatureEnabled(features, required)) return false
  }
  return true
}

export function filterCarouselSlides(
  slides: SilkHomeCarouselSlide[],
  features: TenantFeatures,
): SilkHomeCarouselSlide[] {
  return slides.filter((slide) => isCarouselSlideAllowed(slide, features))
}
